import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bell, Calendar, CheckCircle, Link2, Link2Off, Loader, Lock, RefreshCw, User as UserIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { Header } from '../components/Layout/Header';
import { authApi, integrationsApi, notificationsApi, usersApi } from '../services/api';
import { useAuthStore } from '../store/authStore';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

interface GoogleStatus {
  configured: boolean;
  connected: boolean;
  email: string | null;
  autoSync: boolean;
}

interface NotificationPrefs {
  email_task_assigned: boolean;
  email_task_commented: boolean;
  email_task_due_date: boolean;
  email_project_invited: boolean;
  email_daily_digest: boolean;
  unsubscribed_from_all: boolean;
}

function Toggle({ value, onChange, disabled }: { value: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!value)}
      className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${value ? 'bg-primary-500' : 'bg-gray-300'} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-4' : 'translate-x-0'}`} />
    </button>
  );
}

const defaultPrefs: NotificationPrefs = {
  email_task_assigned: true,
  email_task_commented: true,
  email_task_due_date: true,
  email_project_invited: true,
  email_daily_digest: false,
  unsubscribed_from_all: false,
};

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [googleStatus, setGoogleStatus] = useState<GoogleStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [prefs, setPrefs] = useState<NotificationPrefs>(defaultPrefs);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefsSaving, setPrefsSaving] = useState(false);

  const { user, fetchUser } = useAuthStore();
  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    setProfileName(user?.name || '');
    setProfileEmail(user?.email || '');
  }, [user]);

  const handleSaveProfile = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user) return;
    if (!profileName.trim()) {
      toast.error('Informe um nome válido.');
      return;
    }
    if (!EMAIL_REGEX.test(profileEmail.trim())) {
      toast.error('Informe um e-mail válido.');
      return;
    }
    setProfileSaving(true);
    try {
      await usersApi.update(user.id, { name: profileName.trim(), email: profileEmail.trim() });
      await fetchUser();
      toast.success('Perfil atualizado com sucesso.');
    } catch (err: any) {
      const message = err?.response?.data?.message;
      toast.error(message === 'Email already in use' ? 'Este e-mail já está em uso por outra conta.' : 'Falha ao atualizar perfil.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Preencha todos os campos de senha.');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem.');
      return;
    }
    setPasswordSaving(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      toast.success('Senha alterada com sucesso.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const status = err?.response?.status;
      toast.error(status === 400 ? 'Senha atual incorreta.' : 'Falha ao alterar senha.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const loadStatus = async () => {
    try {
      const res = await integrationsApi.getGoogleStatus();
      setGoogleStatus(res.data);
    } catch {
      setGoogleStatus({ configured: false, connected: false, email: null, autoSync: false });
    } finally {
      setLoading(false);
    }
  };

  const loadPrefs = async () => {
    try {
      const res = await notificationsApi.getPreferences();
      setPrefs({ ...defaultPrefs, ...res.data });
    } catch {
      // uses defaults
    } finally {
      setPrefsLoading(false);
    }
  };

  useEffect(() => {
    const result = searchParams.get('google');
    if (result === 'connected') {
      toast.success('Google Calendar conectado com sucesso!');
      setSearchParams({});
    } else if (result === 'error') {
      toast.error('Falha ao conectar Google Calendar. Tente novamente.');
      setSearchParams({});
    }
    loadStatus();
    loadPrefs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await integrationsApi.getGoogleAuthUrl();
      window.location.href = res.data.url;
    } catch {
      toast.error('Falha ao iniciar conexão com o Google.');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Desconectar o Google Calendar? As tarefas existentes no calendário não serão removidas.')) return;
    setDisconnecting(true);
    try {
      await integrationsApi.disconnectGoogle();
      setGoogleStatus({ configured: true, connected: false, email: null, autoSync: false });
      toast.success('Google Calendar desconectado.');
    } catch {
      toast.error('Falha ao desconectar.');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleToggleSync = async (value: boolean) => {
    try {
      await integrationsApi.toggleGoogleSync(value);
      setGoogleStatus(prev => prev ? { ...prev, autoSync: value } : prev);
      toast.success(value ? 'Sincronização automática ativada.' : 'Sincronização automática desativada.');
    } catch {
      toast.error('Falha ao alterar configuração.');
    }
  };

  const handleTogglePref = async (key: keyof NotificationPrefs, value: boolean) => {
    const updated = { ...prefs, [key]: value };
    if (key === 'unsubscribed_from_all' && value) {
      updated.email_task_assigned = false;
      updated.email_task_commented = false;
      updated.email_task_due_date = false;
      updated.email_project_invited = false;
      updated.email_daily_digest = false;
    } else if (key !== 'unsubscribed_from_all' && value) {
      updated.unsubscribed_from_all = false;
    }
    setPrefs(updated);
    setPrefsSaving(true);
    try {
      await notificationsApi.updatePreferences(updated);
    } catch {
      toast.error('Falha ao salvar preferências.');
      setPrefs(prefs);
    } finally {
      setPrefsSaving(false);
    }
  };

  return (
    <div>
      <Header title="Configurações" />
      <div className="p-6 max-w-2xl">

        <section>
          <h2 className="text-base font-semibold text-gray-900 mb-1">Perfil</h2>
          <p className="text-sm text-gray-500 mb-4">
            Atualize seu nome e e-mail de acesso.
          </p>

          <form onSubmit={handleSaveProfile} className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-3 mb-1">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 border border-primary-100">
                <UserIcon size={20} className="text-primary-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Dados pessoais</h3>
                <p className="text-xs text-gray-500">Nome e e-mail usados para acessar a plataforma.</p>
              </div>
            </div>

            <div>
              <label htmlFor="profile-name" className="block text-xs font-medium text-gray-700 mb-1">Nome</label>
              <input
                id="profile-name"
                type="text"
                value={profileName}
                onChange={e => setProfileName(e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="profile-email" className="block text-xs font-medium text-gray-700 mb-1">E-mail</label>
              <input
                id="profile-email"
                type="email"
                value={profileEmail}
                onChange={e => setProfileEmail(e.target.value)}
                className={inputClass}
              />
            </div>

            <button
              type="submit"
              disabled={profileSaving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors"
            >
              {profileSaving && <Loader size={15} className="animate-spin" />}
              {profileSaving ? 'Salvando...' : 'Salvar'}
            </button>
          </form>
        </section>

        {/* Segurança */}
        <section className="mt-8">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Segurança</h2>
          <p className="text-sm text-gray-500 mb-4">
            Altere a senha usada para acessar sua conta.
          </p>

          <form onSubmit={handleChangePassword} className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-3 mb-1">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 border border-primary-100">
                <Lock size={20} className="text-primary-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Alterar senha</h3>
                <p className="text-xs text-gray-500">Você precisará informar sua senha atual.</p>
              </div>
            </div>

            <div>
              <label htmlFor="current-password" className="block text-xs font-medium text-gray-700 mb-1">Senha atual</label>
              <input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="new-password" className="block text-xs font-medium text-gray-700 mb-1">Nova senha</label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                autoComplete="new-password"
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-xs font-medium text-gray-700 mb-1">Confirmar nova senha</label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                className={inputClass}
              />
            </div>

            <button
              type="submit"
              disabled={passwordSaving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors"
            >
              {passwordSaving && <Loader size={15} className="animate-spin" />}
              {passwordSaving ? 'Salvando...' : 'Alterar senha'}
            </button>
          </form>
        </section>

        <section className="mt-8">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Integrações</h2>
          <p className="text-sm text-gray-500 mb-4">
            Conecte ferramentas externas para sincronizar suas tarefas automaticamente.
          </p>

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-start gap-4">
              {/* Ícone Google Calendar */}
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 border border-primary-100">
                <Calendar size={22} className="text-primary-500" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <h3 className="text-sm font-semibold text-gray-900">Google Calendar</h3>
                  {googleStatus?.connected && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 border border-green-200">
                      <CheckCircle size={10} />
                      Conectado
                    </span>
                  )}
                </div>

                {loading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-400 mt-2">
                    <Loader size={14} className="animate-spin" />
                    Verificando...
                  </div>
                ) : !googleStatus?.configured ? (
                  <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                    <p className="text-xs font-medium text-amber-800">Integração ainda não configurada</p>
                    <p className="text-xs text-amber-700 mt-1">
                      O administrador da plataforma precisa configurar as credenciais OAuth do Google.
                    </p>
                  </div>
                ) : googleStatus.connected ? (
                  <div className="space-y-3 mt-2">
                    <p className="text-xs text-gray-500">
                      Conta: <span className="font-medium text-gray-700">{googleStatus.email}</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      Tarefas com prazo são sincronizadas automaticamente como eventos no seu calendário.
                    </p>

                    {/* Toggle sincronização automática */}
                    <label className="flex items-center gap-3 cursor-pointer">
                      <div
                        onClick={() => handleToggleSync(!googleStatus.autoSync)}
                        className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${
                          googleStatus.autoSync ? 'bg-primary-500' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                            googleStatus.autoSync ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </div>
                      <span className="text-sm text-gray-700">Sincronização automática</span>
                    </label>

                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        onClick={loadStatus}
                        className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 px-2.5 py-1.5 rounded border border-gray-200 hover:bg-gray-50 transition-colors"
                      >
                        <RefreshCw size={12} />
                        Atualizar status
                      </button>
                      <button
                        type="button"
                        onClick={handleDisconnect}
                        disabled={disconnecting}
                        className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 px-2.5 py-1.5 rounded border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-50"
                      >
                        <Link2Off size={12} />
                        {disconnecting ? 'Desconectando...' : 'Desconectar'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 space-y-3">
                    <p className="text-xs text-gray-500">
                      Sincronize suas tarefas com prazo diretamente no Google Calendar. Você receberá notificações da agenda como de costume.
                    </p>
                    <button
                      type="button"
                      onClick={handleConnect}
                      disabled={connecting}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors"
                    >
                      {connecting ? (
                        <Loader size={15} className="animate-spin" />
                      ) : (
                        <Link2 size={15} />
                      )}
                      {connecting ? 'Redirecionando...' : 'Conectar Google Calendar'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Notificações por e-mail */}
        <section className="mt-8">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Notificações por e-mail</h2>
          <p className="text-sm text-gray-500 mb-4">
            Escolha quando você quer receber e-mails do sistema.
          </p>

          <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
            {/* Desativar tudo */}
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3">
                <Bell size={17} className="text-gray-400 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-800">Desativar todos os e-mails</p>
                  <p className="text-xs text-gray-400">Nenhum e-mail será enviado, independente das opções abaixo.</p>
                </div>
              </div>
              {prefsLoading ? (
                <Loader size={14} className="animate-spin text-gray-300" />
              ) : (
                <Toggle
                  value={prefs.unsubscribed_from_all}
                  onChange={(v) => handleTogglePref('unsubscribed_from_all', v)}
                  disabled={prefsSaving}
                />
              )}
            </div>

            {[
              { key: 'email_task_assigned' as const, label: 'Tarefa atribuída a mim', description: 'Quando alguém me atribui uma nova tarefa.' },
              { key: 'email_task_commented' as const, label: 'Comentário em minha tarefa', description: 'Quando alguém comenta em uma tarefa que me pertence.' },
              { key: 'email_task_due_date' as const, label: 'Prazo se aproximando', description: 'Lembrete 24 horas antes do vencimento.' },
              { key: 'email_project_invited' as const, label: 'Adicionado a um projeto', description: 'Quando sou incluído em um novo projeto.' },
              { key: 'email_daily_digest' as const, label: 'Resumo diário', description: 'Um e-mail diário com o resumo das suas tarefas.' },
            ].map(({ key, label, description }) => (
              <div key={key} className={`flex items-center justify-between px-5 py-4 ${prefs.unsubscribed_from_all ? 'opacity-40' : ''}`}>
                <div>
                  <p className="text-sm font-medium text-gray-800">{label}</p>
                  <p className="text-xs text-gray-400">{description}</p>
                </div>
                {prefsLoading ? (
                  <Loader size={14} className="animate-spin text-gray-300" />
                ) : (
                  <Toggle
                    value={prefs[key]}
                    onChange={(v) => handleTogglePref(key, v)}
                    disabled={prefsSaving || prefs.unsubscribed_from_all}
                  />
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Seção futura: Evolution API / WhatsApp */}
        <section className="mt-8">
          <div className="bg-white border border-dashed border-gray-200 rounded-xl p-5 opacity-60">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-50 border border-green-100">
                <span className="text-lg">💬</span>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">WhatsApp <span className="text-xs font-normal text-gray-400 ml-1">— em breve</span></h3>
                <p className="text-xs text-gray-400 mt-0.5">Notificações via Evolution API</p>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
